import { CAT_RUNNING_ASSETS } from '../assets'
import { CAT_RUNNING_CHARACTER_KEY, CAT_RUNNING_STAGE_KEY } from '../constants'

export const createImageRecord = (src) => ({
  src,
  image: null,
  isLoaded: false,
})

export const preloadImageRecord = (record) =>
  new Promise((resolve) => {
    if (!record?.src) {
      resolve(record)
      return
    }

    const image = new Image()
    let isResolved = false
    const finalize = () => {
      if (isResolved) return
      isResolved = true
      record.image = image
      record.isLoaded = true
      resolve(record)
    }

    image.onload = () => {
      if (typeof image.decode === 'function') {
        image
          .decode()
          .catch(() => null)
          .finally(finalize)
        return
      }

      finalize()
    }

    image.onerror = () => resolve(record)
    image.src = record.src

    if (image.complete) {
      finalize()
    }
  })

export const createAssetRecords = (assets, stageKey, characterKey) => ({
  background: createImageRecord(assets.backgrounds[stageKey]),
  characterRun: assets.characters[characterKey].run.map((src) => createImageRecord(src)),
  characterDeath: createImageRecord(assets.characters[characterKey].death),
  groundObstacles: assets.obstacles[stageKey].map((src) => createImageRecord(src)),
  crowFrames: assets.obstacles.common.crow.map((src) => createImageRecord(src)),
  churu: createImageRecord(assets.items.churu),
  gameOver: createImageRecord(assets.ui.gameOver),
})

export const preloadGameAssetRecords = async (assetRecords) => {
  const records = await Promise.all([
    preloadImageRecord(assetRecords.background),
    ...assetRecords.characterRun.map((record) => preloadImageRecord(record)),
    preloadImageRecord(assetRecords.characterDeath),
    ...assetRecords.groundObstacles.map((record) => preloadImageRecord(record)),
    ...assetRecords.crowFrames.map((record) => preloadImageRecord(record)),
    preloadImageRecord(assetRecords.churu),
    preloadImageRecord(assetRecords.gameOver),
  ])

  const [background, ...restRecords] = records
  const runFrames = restRecords.slice(0, assetRecords.characterRun.length)
  const deathFrame = restRecords[assetRecords.characterRun.length]
  const groundObstacleStart = assetRecords.characterRun.length + 1
  const crowFrameStart = groundObstacleStart + assetRecords.groundObstacles.length
  const crowFrameEnd = crowFrameStart + assetRecords.crowFrames.length
  const obstacleFrames = restRecords.slice(groundObstacleStart, crowFrameStart)
  const crowFrames = restRecords.slice(crowFrameStart, crowFrameEnd)
  const churuRecord = restRecords[crowFrameEnd]
  const gameOverFrame = restRecords[restRecords.length - 1]

  return {
    backgroundReady: background.isLoaded,
    characterReady: runFrames.every((frame) => frame.isLoaded) && deathFrame?.isLoaded === true,
    obstacleReady: obstacleFrames.every((frame) => frame.isLoaded) && crowFrames.every((frame) => frame.isLoaded),
    itemReady: churuRecord?.isLoaded === true,
    uiReady: gameOverFrame?.isLoaded === true,
  }
}

let _sharedRecords = null
let _sharedPreloadPromise = null

export const getSharedCatRunningAssets = () => {
  if (!_sharedRecords) {
    _sharedRecords = createAssetRecords(
      CAT_RUNNING_ASSETS,
      CAT_RUNNING_STAGE_KEY,
      CAT_RUNNING_CHARACTER_KEY,
    )
  }
  return _sharedRecords
}

export const preloadSharedCatRunningAssets = () => {
  if (!_sharedPreloadPromise) {
    _sharedPreloadPromise = preloadGameAssetRecords(getSharedCatRunningAssets())
  }
  return _sharedPreloadPromise
}
